import type { CSSProperties, ReactNode } from "react";
import { CMSComponent, CMSComponents, type CMSComponentProps } from "../engine/CMSComponent.tsx";

const str = (value: unknown) => (typeof value === "string" ? value : undefined);

/** "blue 600" -> "theme-blue-600" */
export const themeClass = (theme: unknown) =>
  str(theme) ? `theme-${str(theme)!.toLowerCase().replace(/\s+/g, "-")}` : "";

export function SectionShell({
  node,
  className = "",
  children,
}: CMSComponentProps & { className?: string; children: ReactNode }) {
  // "default" (or no value) leaves spacing to the stylesheet
  const spacer = (value: unknown) => (str(value) && value !== "default" ? str(value) : undefined);
  const style = {
    "--section-top": spacer(node.topSpacer),
    "--section-bottom": spacer(node.bottomSpacer),
  } as CSSProperties;

  return (
    <section
      id={str(node.componentId)}
      className={`cms-section ${themeClass(node.theme)} ${className}`}
      data-component-type={node.componentType}
      style={style}
    >
      <div className="cms-container">{children}</div>
    </section>
  );
}

/**
 * Generic section layout shared by most *Section / *SectionType components:
 * heading text, optional media, then a grid of collection items.
 */
export default function Section({ node }: CMSComponentProps) {
  const { textGroup, headline, mediaFrame, collection } = node;
  const hasMedia = Boolean(mediaFrame);
  const imageFirst = /left|start/i.test(str(node.imagePosition) ?? "");

  return (
    <SectionShell node={node}>
      <div className={hasMedia ? "cms-split" : undefined}>
        <div className="cms-section__text">
          {Array.isArray(textGroup) ? (
            <CMSComponents nodes={textGroup} />
          ) : (
            <CMSComponent node={textGroup} />
          )}
          <CMSComponent node={headline} />
        </div>
        {hasMedia && <CMSComponent node={mediaFrame} />}
      </div>
      {Array.isArray(collection) && (
        <div
          className={`cms-grid ${imageFirst ? "cms-grid--image-first" : ""}`}
          style={{ "--columns": str(node.maxColumns) ?? "3" } as CSSProperties}
        >
          <CMSComponents nodes={collection} />
        </div>
      )}
    </SectionShell>
  );
}

export function LogoTickerSection({ node }: CMSComponentProps) {
  return (
    <SectionShell node={node} className="cms-logos">
      <CMSComponents nodes={node.collection} />
    </SectionShell>
  );
}

export function HomepageHero({ node }: CMSComponentProps) {
  const hasTabs = Boolean(str(node.tab1Text) && str(node.tab2Text));
  return (
    <SectionShell node={node} className="cms-hero">
      {hasTabs && (
        <nav className="cms-hero__tabs" aria-label={str(node.segmentControlGroupLabel)}>
          <a href={`/${str(node.tab1Link) ?? ""}`}>{str(node.tab1Text)}</a>
          <a href={`/${str(node.tab2Link) ?? ""}`} aria-current="page">
            {str(node.tab2Text)}
          </a>
        </nav>
      )}
      <CMSComponent node={node.textGroup} />
    </SectionShell>
  );
}

/** A card or column inside a section grid. */
export function Item({ node }: CMSComponentProps) {
  const headline = str(node.headline);
  return (
    <article className={`cms-item ${themeClass(node.cardTheme)}`}>
      <CMSComponent node={node.logo} />
      <CMSComponent node={node.mediaFrame} />
      {headline && <h3 className="cms-item__headline">{headline}</h3>}
      <CMSComponent node={node.textGroup ?? node.paragraph} />
    </article>
  );
}

/** Passes through to the referenced component (PageSegmentItem, TextHeaderInner). */
export function Reference({ node }: CMSComponentProps) {
  return <CMSComponent node={node.componentReference ?? node.textGroup} />;
}
